import { openDB, type DBSchema, type IDBPDatabase, type IDBPObjectStore, type StoreNames } from "idb";
import {
  validAnnotationEnvelope,
  validBriefing,
  validBriefingEnvelope,
  validDaily,
  validInboxBrief,
  validInboxBriefEnvelope,
  validItemList,
  validPodcastEpisode,
  validPodcastEpisodeEnvelope,
  validReaderItem,
  validSearchItemList,
  validCanonicalItemId,
  validArtworkPath,
} from "./api";
import { isCanonicalUserId, isStructurallyValidSession } from "./auth";
import { isJsonObject, REQUEST_TIMEOUT_MS } from "./json";
import { withSameOriginSessionTransitionLock, type SessionTransitionLock } from "./sessionState";
import type {
  AnnotationEnvelope,
  Briefing,
  BriefingEnvelope,
  DailyEditionEnvelope,
  InboxBrief,
  InboxBriefEnvelope,
  ItemListEnvelope,
  OfflineSyncState,
  PodcastEpisode,
  PodcastEpisodeEnvelope,
  ReaderItem,
  ReaderPreferences,
  ReadingSessionInput,
  Session,
} from "./types";

interface ArtworkRecord {
  path: string;
  blob: Blob;
  byteCount: number;
  lastAccessedAt: string;
}

interface ArtworkCacheEntry {
  path: string;
  byteCount: number;
  lastAccessedAt: string;
}

interface ArtworkCacheIndexRecord {
  key: "artworkCacheIndex";
  version: 1;
  entries: ArtworkCacheEntry[];
}

interface SessionRecord {
  key: "session";
  session: Session;
  generation?: string;
}

interface OfflineCatalogRecord {
  key: "offlineCatalog";
  cachedAt: string;
}

type MetaRecord = SessionRecord | ArtworkCacheIndexRecord | OfflineCatalogRecord;

export interface OfflineSessionContext {
  session: Session;
  generation: string;
}

interface ItemTombstoneRecord {
  key: string;
  userId: string;
  itemId: string;
  deletedAt: string;
}

interface OutboxRecord {
  key: string;
  version?: string;
  userId?: string;
  kind: "progress" | "session" | "state" | "annotation";
  itemId: string;
  payload: { reading_progress: number } | ReadingSessionInput | { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean } | { annotation_id: string; note: string };
  createdAt: string;
  leaseOwner?: string;
  leaseExpiresAt?: number;
}

interface CatalogRecord {
  id: string;
  item: ReaderItem;
  contentVersion: string;
  markdownByteCount: number;
  cachedAt: string;
}

interface SyncRecord {
  key: "state";
  state: OfflineSyncState;
}

type ClaimedOutboxRecord = OutboxRecord & { version: string; leaseOwner: string; leaseExpiresAt: number };

interface ImprintDB extends DBSchema {
  lists: { key: string; value: { key: string; envelope: ItemListEnvelope; cachedAt: string } };
  daily: { key: string; value: { key: string; envelope: DailyEditionEnvelope; cachedAt: string } };
  articles: { key: string; value: { id: string; item: ReaderItem; cachedAt: string } };
  annotations: { key: string; value: { itemId: string; envelope: AnnotationEnvelope; cachedAt: string } };
  outbox: { key: string; value: OutboxRecord };
  meta: { key: string; value: MetaRecord };
  inboxBriefs: { key: string; value: { key: string; envelope: InboxBriefEnvelope; cachedAt: string } };
  inboxBriefDetails: { key: string; value: { id: string; brief: InboxBrief; cachedAt: string } };
  podcastEpisodes: { key: string; value: { key: string; envelope: PodcastEpisodeEnvelope; cachedAt: string } };
  podcastEpisodeDetails: { key: string; value: { id: string; episode: PodcastEpisode; cachedAt: string } };
  artwork: { key: string; value: ArtworkRecord };
  briefings: { key: string; value: { key: string; envelope: BriefingEnvelope; cachedAt: string } };
  briefingDetails: { key: string; value: { id: string; briefing: Briefing; cachedAt: string } };
  itemTombstones: { key: string; value: ItemTombstoneRecord };
  catalog: { key: string; value: CatalogRecord };
  sync: { key: string; value: SyncRecord };
}

const DB_NAME = "imprint-reader-v1";
const DB_VERSION = 8;
const DB_OPEN_TIMEOUT_MS = 5_000;
const PREFS_KEY = "imprint.reader.preferences.v1";
const DEFAULT_PREFERENCES: ReaderPreferences = { typeSize: "m", width: "narrow", theme: "paper" };
const MAX_ARTWORK_CACHE_BYTES = 64 * 1024 * 1024;
const MAX_ARTWORK_DIMENSION = 8_192;
const MAX_ARTWORK_PIXELS = 16_000_000;
const MAX_JPEG_HEADER_BYTES = 64 * 1024;
const MAX_PNG_CHUNKS = 4_096;
const MAX_PROGRESS_RECORDS = 100;
const MAX_SESSION_RECORDS = 50;
const MAX_MUTATION_RECORDS = 200;
const MAX_OFFLINE_CATALOG_ITEMS = 5_000;
const OUTBOX_LEASE_DURATION_MS = 60_000;
const MIN_OUTBOX_LEASE_DURATION_MS = REQUEST_TIMEOUT_MS + 5_000;
const MAX_OUTBOX_LEASE_DURATION_MS = 5 * 60_000;
const MAX_ARTWORK_RECORDS = 1_024;
const MAX_DELETION_LIST_RECORDS = 512;
const MAX_DELETION_DAILY_RECORDS = 512;
const OUTBOX_LOCK_NAME = "imprint-reader-outbox-v1";
const ARTWORK_CACHE_INDEX_KEY = "artworkCacheIndex";
const PNG_CRC_TABLE = new Uint32Array(256);
for (let value = 0; value < PNG_CRC_TABLE.length; value += 1) {
  let crc = value;
  for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb8_8320 : 0);
  PNG_CRC_TABLE[value] = crc >>> 0;
}
const ALL_STORE_NAMES = [
  "lists", "daily", "articles", "annotations", "outbox", "meta", "inboxBriefs", "inboxBriefDetails",
  "podcastEpisodes", "podcastEpisodeDetails", "artwork", "briefings", "briefingDetails", "itemTombstones",
  "catalog", "sync",
] as const;

export function openImprintDatabase(
  name = DB_NAME,
  version = DB_VERSION,
  timeoutMs = DB_OPEN_TIMEOUT_MS,
): Promise<IDBPDatabase<ImprintDB>> {
  if (!name || !Number.isSafeInteger(version) || version < 1 || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return Promise.reject(new TypeError("invalid_indexeddb_open_options"));
  }

  let connection: IDBPDatabase<ImprintDB> | undefined;
  let settled = false;
  let upgradeBlocked = false;
  let resolveBounded!: (database: IDBPDatabase<ImprintDB>) => void;
  let rejectBounded!: (error: unknown) => void;
  let timeout = 0;
  const bounded = new Promise<IDBPDatabase<ImprintDB>>((resolve, reject) => {
    resolveBounded = resolve;
    rejectBounded = reject;
  });
  const fail = (error: unknown) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeout);
    rejectBounded(error);
  };
  timeout = window.setTimeout(
    () => fail(new Error(upgradeBlocked ? "indexeddb_upgrade_blocked" : "indexeddb_open_timeout")),
    timeoutMs,
  );
  const opened = openDB<ImprintDB>(name, version, {
    upgrade: upgradeImprintDatabase,
    blocked() { upgradeBlocked = true; },
    blocking() { connection?.close(); },
    terminated() { fail(new Error("indexeddb_connection_terminated")); },
  });
  void opened.then((database) => {
    connection = database;
    if (settled) {
      database.close();
      return;
    }
    settled = true;
    window.clearTimeout(timeout);
    resolveBounded(database);
  }, fail);
  return bounded;
}

function upgradeImprintDatabase(database: IDBPDatabase<ImprintDB>): void {
  if (!database.objectStoreNames.contains("lists")) database.createObjectStore("lists", { keyPath: "key" });
  if (!database.objectStoreNames.contains("daily")) database.createObjectStore("daily", { keyPath: "key" });
  if (!database.objectStoreNames.contains("articles")) database.createObjectStore("articles", { keyPath: "id" });
  if (!database.objectStoreNames.contains("annotations")) database.createObjectStore("annotations", { keyPath: "itemId" });
  if (!database.objectStoreNames.contains("outbox")) database.createObjectStore("outbox", { keyPath: "key" });
  if (!database.objectStoreNames.contains("meta")) database.createObjectStore("meta", { keyPath: "key" });
  if (!database.objectStoreNames.contains("inboxBriefs")) database.createObjectStore("inboxBriefs", { keyPath: "key" });
  if (!database.objectStoreNames.contains("inboxBriefDetails")) database.createObjectStore("inboxBriefDetails", { keyPath: "id" });
  if (!database.objectStoreNames.contains("podcastEpisodes")) database.createObjectStore("podcastEpisodes", { keyPath: "key" });
  if (!database.objectStoreNames.contains("podcastEpisodeDetails")) database.createObjectStore("podcastEpisodeDetails", { keyPath: "id" });
  if (!database.objectStoreNames.contains("artwork")) database.createObjectStore("artwork", { keyPath: "path" });
  if (!database.objectStoreNames.contains("briefings")) database.createObjectStore("briefings", { keyPath: "key" });
  if (!database.objectStoreNames.contains("briefingDetails")) database.createObjectStore("briefingDetails", { keyPath: "id" });
  if (!database.objectStoreNames.contains("itemTombstones")) database.createObjectStore("itemTombstones", { keyPath: "key" });
  if (!database.objectStoreNames.contains("catalog")) database.createObjectStore("catalog", { keyPath: "id" });
  if (!database.objectStoreNames.contains("sync")) database.createObjectStore("sync", { keyPath: "key" });
}

const rawDbPromise = openImprintDatabase();

// Authentication may deliberately publish an online session before its offline
// owner record has finished persisting. Register that transition synchronously
// so account-bound storage started immediately afterward cannot observe or write
// through the previous owner's fence. Keep a recovered tail for waiters: the
// initiating caller still receives persistence failures, while a failed attempt
// cannot permanently wedge later cache work.
let sessionContextTransitionTail: Promise<void> = Promise.resolve();

function enqueueSessionContextTransition(operation: () => Promise<void>): Promise<void> {
  const transition = sessionContextTransitionTail.then(operation);
  sessionContextTransitionTail = transition.then(() => undefined, () => undefined);
  return transition;
}

async function databaseAfterLatestSessionContextTransition(): Promise<IDBPDatabase<ImprintDB>> {
  const database = await rawDbPromise;
  while (true) {
    const transition = sessionContextTransitionTail;
    await transition;
    if (transition === sessionContextTransitionTail) return database;
  }
}

// Keep the existing database access sites honest by making every await observe
// the current transition tail rather than capturing a one-time readiness promise.
const dbPromise: PromiseLike<IDBPDatabase<ImprintDB>> = {
  then<TResult1 = IDBPDatabase<ImprintDB>, TResult2 = never>(
    onfulfilled?: ((value: IDBPDatabase<ImprintDB>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return databaseAfterLatestSessionContextTransition().then(onfulfilled, onrejected);
  },
};

export async function cacheOfflineSession(session: Session): Promise<void> {
  if (!validStoredSession(session)) throw new TypeError("invalid_session");
  return enqueueSessionContextTransition(() => persistOfflineSession(session));
}

async function persistOfflineSession(session: Session): Promise<void> {
  // This operation owns sessionContextTransitionTail and must use rawDbPromise
  // directly; awaiting the public readiness barrier here would deadlock.
  const database = await rawDbPromise;
  const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
  const existing: unknown = await transaction.objectStore("meta").get("session");
  if (!sessionBelongsTo(existing, session.user_id)) {
    await Promise.all(ALL_STORE_NAMES.map((name) => transaction.objectStore(name).clear()));
  }
  const generation = storedSessionMatches(existing, session) && sessionGeneration(existing)
    ? sessionGeneration(existing) as string
    : newSessionGeneration();
  await transaction.objectStore("meta").put({ key: "session", session, generation });
  await transaction.done;
}

export async function cachedOfflineSession(): Promise<Session | undefined> {
  return (await cachedOfflineSessionContext())?.session;
}

export async function cachedOfflineSessionContext(): Promise<OfflineSessionContext | undefined> {
  const database = await databaseAfterLatestSessionContextTransition();
  const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
  const record: unknown = await transaction.objectStore("meta").get("session");
  if (!isJsonObject(record) || record.key !== "session" || !validStoredSession(record.session)) {
    await Promise.all(ALL_STORE_NAMES.map((name) => transaction.objectStore(name).clear()));
    await transaction.done;
    return undefined;
  }
  const generation = sessionGeneration(record) || newSessionGeneration();
  if (record.generation !== generation) {
    await transaction.objectStore("meta").put({ key: "session", session: record.session, generation });
  }
  await transaction.done;
  return { session: record.session, generation };
}

function validStoredSession(value: unknown): value is Session {
  return isStructurallyValidSession(value);
}

function sessionBelongsTo(record: unknown, userId: string, generation?: string): boolean {
  return Boolean(userId
    && isJsonObject(record)
    && record.key === "session"
    && validStoredSession(record.session)
    && record.session.user_id === userId
    && (generation === undefined || sessionGeneration(record) === generation));
}

function storedSessionMatches(record: unknown, session: Session): boolean {
  if (!isJsonObject(record) || record.key !== "session" || !validStoredSession(record.session)) return false;
  return record.session.user_id === session.user_id
    && record.session.email === session.email
    && record.session.display_name === session.display_name
    && record.session.expires_at === session.expires_at;
}

function sessionGeneration(record: unknown): string | undefined {
  if (!isJsonObject(record)) return undefined;
  return validSessionGeneration(record.generation)
    ? record.generation
    : undefined;
}

function validSessionGeneration(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{1,128}$/.test(value);
}

function newSessionGeneration(): string {
  return crypto.randomUUID();
}

function validCachedAt(value: unknown): boolean {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

export async function cacheList(userId: string, key: string, envelope: ItemListEnvelope): Promise<void> {
  if (!validListForCacheKey(key, envelope as unknown as Record<string, unknown>)) throw new TypeError("invalid_list_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "lists", "itemTombstones"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    const safeEnvelope = await withoutTombstonedListItems(transaction.objectStore("itemTombstones"), userId, envelope);
    await transaction.objectStore("lists").put({ key, envelope: safeEnvelope, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedList(userId: string, key: string): Promise<ItemListEnvelope | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "lists", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("lists").get(key);
  if (!isJsonObject(record) || record.key !== key || !validCachedAt(record.cachedAt) || !isJsonObject(record.envelope) || !validListForCacheKey(key, record.envelope)) {
    if (record !== undefined) await transaction.objectStore("lists").delete(key);
    await transaction.done;
    return undefined;
  }
  const cachedEnvelope = record.envelope as unknown as ItemListEnvelope;
  const envelope = await withoutTombstonedListItems(
    transaction.objectStore("itemTombstones"),
    userId,
    cachedEnvelope,
  );
  if (envelope !== cachedEnvelope) {
    await transaction.objectStore("lists").put({ key, envelope, cachedAt: record.cachedAt as string });
  }
  await transaction.done;
  return envelope;
}

export async function replaceOfflineCatalog(userId: string, items: ReaderItem[]): Promise<void> {
  if (!isCanonicalUserId(userId)
    || items.length > MAX_OFFLINE_CATALOG_ITEMS
    || items.some((item) => !validReaderItem(item)
      || typeof item.content_version !== "string"
      || item.content_version.length > 128
      || !Number.isSafeInteger(item.markdown_byte_count)
      || (item.markdown_byte_count as number) < 0
      || (item.markdown_byte_count as number) > 8 * 1024 * 1024)
    || new Set(items.map((item) => item.id)).size !== items.length) throw new TypeError("invalid_offline_catalog");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "catalog", "articles", "annotations", "outbox", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return; }
  const catalog = transaction.objectStore("catalog");
  const outboxRecords = await validOutboxRecords(transaction.objectStore("outbox"), userId);
  const pendingStates = pendingItemUpdates(outboxRecords);
  const currentKeys = await catalog.getAllKeys();
  const nextIds = new Set(items.map((item) => item.id));
  const removedIds = currentKeys.filter((key): key is string => typeof key === "string" && !nextIds.has(key));
  await catalog.clear();
  const cachedAt = new Date().toISOString();
  for (const item of items) {
    const { markdown: _markdown, ...summary } = item;
    const localState = pendingStates.get(item.id);
    await catalog.put({
      id: item.id,
      item: localState ? { ...summary, ...localState } : summary,
      contentVersion: item.content_version || "",
      markdownByteCount: item.markdown_byte_count || 0,
      cachedAt,
    });
  }
  for (const id of removedIds) {
    await transaction.objectStore("articles").delete(id);
    await transaction.objectStore("annotations").delete(id);
    await transaction.objectStore("itemTombstones").delete(id);
    for (const record of outboxRecords.filter((value) => value.itemId === id)) {
      await transaction.objectStore("outbox").delete(record.key);
    }
  }
  await transaction.objectStore("meta").put({ key: "offlineCatalog", cachedAt });
  await transaction.done;
}

export async function cachedOfflineCatalog(
  userId: string,
  section: "library" | "starred" | "archive" | "queue" | "inbox",
): Promise<ItemListEnvelope | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "catalog", "itemTombstones"], "readonly");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const records = await transaction.objectStore("catalog").getAll();
  const catalogState = await transaction.objectStore("meta").get("offlineCatalog");
  const tombstones = new Set((await transaction.objectStore("itemTombstones").getAll())
    .filter((record) => record.userId === userId)
    .map((record) => record.itemId));
  await transaction.done;
  if (!offlineCatalogExists(records.length, catalogState)) return undefined;
  let items = records.map((record) => record.item).filter((item) => !tombstones.has(item.id));
  if (section === "library") items = items.filter((item) => item.status === "inbox" || item.status === "queue");
  else if (section === "starred") items = items.filter((item) => item.is_favorite && item.status !== "archive");
  else items = items.filter((item) => item.status === section);
  items.sort((left, right) => offlineCatalogPosition(right, section).localeCompare(offlineCatalogPosition(left, section)) || right.id.localeCompare(left.id));
  return { ok: true, items, has_more: false, next_cursor: null };
}

export async function cachedOfflineSearch(userId: string, rawQuery: string): Promise<ItemListEnvelope | undefined> {
  const query = rawQuery.trim().replace(/\s+/g, " ").toLocaleLowerCase();
  if (!query) return { ok: true, items: [], has_more: false, next_cursor: null };
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "catalog", "articles", "itemTombstones"], "readonly");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const [records, articles, tombstoneRecords, catalogState] = await Promise.all([
    transaction.objectStore("catalog").getAll(),
    transaction.objectStore("articles").getAll(),
    transaction.objectStore("itemTombstones").getAll(),
    transaction.objectStore("meta").get("offlineCatalog"),
  ]);
  await transaction.done;
  if (!offlineCatalogExists(records.length, catalogState)) return undefined;
  const bodies = new Map(articles.map((record) => [record.id, record.item.markdown || ""]));
  const tombstones = new Set(tombstoneRecords.filter((record) => record.userId === userId).map((record) => record.itemId));
  const items = records
    .filter((record) => !tombstones.has(record.id) && offlineSearchText(record.item, bodies.get(record.id) || "").includes(query))
    .map((record) => record.item);
  return { ok: true, items, has_more: false, next_cursor: null, query: rawQuery, search_status: "complete", search_complete: true, search_truncated: false, search_truncation_reason: "", search_truncation_reasons: [], search_scanned_count: records.length, search_scan_limit: MAX_OFFLINE_CATALOG_ITEMS };
}

function offlineCatalogExists(recordCount: number, state: unknown): boolean {
  // Populated catalogs from earlier versions predate the explicit empty-catalog marker.
  return recordCount > 0 || (isJsonObject(state) && state.key === "offlineCatalog" && validCachedAt(state.cachedAt));
}

export async function cachedOfflineBodyVersions(userId: string): Promise<Map<string, string>> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "articles"], "readonly");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return new Map(); }
  const records = await transaction.objectStore("articles").getAll();
  await transaction.done;
  return new Map(records.map((record) => [record.id, record.item.content_version || record.item.extraction_id || ""]));
}

export async function saveOfflineSyncState(userId: string, state: OfflineSyncState): Promise<void> {
  if (!isCanonicalUserId(userId) || !validOfflineSyncState(state)) throw new TypeError("invalid_offline_sync_state");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "sync"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) await transaction.objectStore("sync").put({ key: "state", state });
  await transaction.done;
}

export async function loadOfflineSyncState(userId: string): Promise<OfflineSyncState | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "sync"], "readonly");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("sync").get("state");
  await transaction.done;
  return isJsonObject(record) && record.key === "state" && validOfflineSyncState(record.state)
    ? record.state as unknown as OfflineSyncState
    : undefined;
}

function offlineCatalogPosition(item: ReaderItem, section: string): string {
  const record = item as ReaderItem & { library_position?: string; inbox_position?: string; created_at?: string };
  return section === "inbox" ? record.inbox_position || record.created_at || "" : record.library_position || record.created_at || item.updated_at || "";
}

function offlineSearchText(item: ReaderItem, markdown: string): string {
  return [item.title, item.site_name, item.author_name, item.byline, item.excerpt, ...(item.tags || []).map((tag) => tag.name), markdown]
    .filter(Boolean)
    .join("\n")
    .toLocaleLowerCase();
}

function validOfflineSyncState(value: unknown): value is OfflineSyncState {
  if (!isJsonObject(value)
    || !["idle", "syncing", "ready", "partial", "error"].includes(String(value.status))
    || !Number.isSafeInteger(value.item_count) || (value.item_count as number) < 0
    || !Number.isSafeInteger(value.body_count) || (value.body_count as number) < 0
    || !Number.isSafeInteger(value.total_body_bytes) || (value.total_body_bytes as number) < 0
    || typeof value.completed_at !== "string"
    || (value.completed_at !== "" && !Number.isFinite(Date.parse(value.completed_at)))
    || typeof value.message !== "string" || value.message.length > 500) return false;
  return (value.usage_bytes === undefined || (Number.isSafeInteger(value.usage_bytes) && (value.usage_bytes as number) >= 0))
    && (value.quota_bytes === undefined || (Number.isSafeInteger(value.quota_bytes) && (value.quota_bytes as number) >= 0))
    && (value.persistent === undefined || typeof value.persistent === "boolean");
}

function validListForCacheKey(key: string, envelope: Record<string, unknown>): boolean {
  return key.startsWith("search:")
    ? validSearchItemList(envelope, 256, key.slice("search:".length))
    : validItemList(envelope, 256);
}

type TombstoneStore = { get(key: string): Promise<unknown> };

function tombstoneFences(value: unknown, userId: string, itemId: string): boolean {
  if (value === undefined) return false;
  if (!isJsonObject(value)
    || value.key !== itemId
    || value.itemId !== itemId
    || !validCanonicalItemId(value.itemId)
    || !isCanonicalUserId(value.userId)
    || !validCachedAt(value.deletedAt)) return true;
  return value.userId === userId;
}

async function tombstonedIds(store: TombstoneStore, userId: string, ids: string[]): Promise<Set<string>> {
  const uniqueIds = [...new Set(ids)];
  const records = await Promise.all(uniqueIds.map((id) => store.get(id)));
  return new Set(uniqueIds.filter((id, index) => tombstoneFences(records[index], userId, id)));
}

async function withoutTombstonedListItems(store: TombstoneStore, userId: string, envelope: ItemListEnvelope): Promise<ItemListEnvelope> {
  const deleted = await tombstonedIds(store, userId, [
    ...envelope.items.map((item) => item.id),
    ...(envelope.results || []).map((item) => item.id),
  ]);
  if (!deleted.size) return envelope;
  return {
    ...envelope,
    items: envelope.items.filter((item) => !deleted.has(item.id)),
    ...(envelope.results ? { results: envelope.results.filter((item) => !deleted.has(item.id)) } : {}),
  };
}

async function withoutTombstonedDailyItems(store: TombstoneStore, userId: string, envelope: DailyEditionEnvelope): Promise<DailyEditionEnvelope> {
  const ids = dailyItemIds(envelope);
  const deleted = await tombstonedIds(store, userId, ids);
  let safe = envelope;
  for (const id of deleted) safe = removeItemFromDaily(safe, id);
  return safe;
}

function dailyItemIds(envelope: DailyEditionEnvelope): string[] {
  const ids = envelope.entries.map((entry) => entry.item_id);
  for (const item of envelope.issue?.items || []) ids.push(item.item_id);
  for (const section of envelope.issue?.sections || []) {
    ids.push(...(section.item_ids || []));
    for (const item of section.items || []) ids.push(item.item_id);
  }
  return ids;
}

function removeItemFromDaily(envelope: DailyEditionEnvelope, itemId: string): DailyEditionEnvelope {
  return {
    ...envelope,
    entries: envelope.entries.filter((entry) => entry.item_id !== itemId),
    issue: envelope.issue ? {
      ...envelope.issue,
      ...(envelope.issue.items ? { items: envelope.issue.items.filter((item) => item.item_id !== itemId) } : {}),
      ...(envelope.issue.sections ? {
        sections: envelope.issue.sections.map((section) => ({
          ...section,
          ...(section.item_ids ? { item_ids: section.item_ids.filter((id) => id !== itemId) } : {}),
          ...(section.items ? { items: section.items.filter((item) => item.item_id !== itemId) } : {}),
        })).filter((section) => (section.item_ids?.length || 0) + (section.items?.length || 0) > 0),
      } : {}),
    } : null,
  };
}

export async function cacheBriefings(userId: string, envelope: BriefingEnvelope): Promise<void> {
  if (!validBriefingEnvelope(envelope as unknown as Record<string, unknown>)) throw new TypeError("invalid_briefings_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "briefings"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.objectStore("briefings").put({ key: "history", envelope, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cacheCurrentBriefing(userId: string, briefing: Briefing): Promise<void> {
  if (!validBriefing(briefing)) throw new TypeError("invalid_briefing_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "briefingDetails"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.objectStore("briefingDetails").put({ id: "__current__", briefing, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedCurrentBriefing(userId: string): Promise<Briefing | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "briefingDetails"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("briefingDetails").get("__current__");
  if (!isJsonObject(record) || record.id !== "__current__" || !validCachedAt(record.cachedAt) || !validBriefing(record.briefing)) {
    if (record !== undefined) await transaction.objectStore("briefingDetails").delete("__current__");
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.briefing;
}

export async function cachedBriefings(userId: string): Promise<BriefingEnvelope | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "briefings"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("briefings").get("history");
  if (!isJsonObject(record) || record.key !== "history" || !validCachedAt(record.cachedAt) || !isJsonObject(record.envelope) || !validBriefingEnvelope(record.envelope)) {
    if (record !== undefined) await transaction.objectStore("briefings").delete("history");
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.envelope as unknown as BriefingEnvelope;
}

export async function cacheBriefing(userId: string, briefing: Briefing): Promise<void> {
  if (!validBriefing(briefing)) throw new TypeError("invalid_briefing_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "briefingDetails"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.objectStore("briefingDetails").put({ id: briefing.id, briefing, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedBriefing(userId: string, id: string): Promise<Briefing | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "briefingDetails"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("briefingDetails").get(id);
  if (!isJsonObject(record) || record.id !== id || !validCachedAt(record.cachedAt) || !validBriefing(record.briefing) || record.briefing.id !== id) {
    if (record !== undefined) await transaction.objectStore("briefingDetails").delete(id);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.briefing;
}

export async function cacheDaily(userId: string, key: string, envelope: DailyEditionEnvelope): Promise<void> {
  if (!validDaily(envelope as unknown as Record<string, unknown>) || !dailyEnvelopeMatchesKey(envelope, key)) {
    throw new TypeError("invalid_daily_cache");
  }
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "daily", "itemTombstones"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    const safeEnvelope = await withoutTombstonedDailyItems(transaction.objectStore("itemTombstones"), userId, envelope);
    if (validDaily(safeEnvelope as unknown as Record<string, unknown>)) {
      await transaction.objectStore("daily").put({ key, envelope: safeEnvelope, cachedAt: new Date().toISOString() });
    } else {
      await transaction.objectStore("daily").delete(key);
    }
  }
  await transaction.done;
}

export async function cachedDaily(userId: string, key: string): Promise<DailyEditionEnvelope | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "daily", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("daily").get(key);
  if (!isJsonObject(record)
    || record.key !== key
    || !validCachedAt(record.cachedAt)
    || !isJsonObject(record.envelope)
    || !validDaily(record.envelope)
    || !dailyEnvelopeMatchesKey(record.envelope, key)) {
    if (record !== undefined) await transaction.objectStore("daily").delete(key);
    await transaction.done;
    return undefined;
  }
  const cachedEnvelope = record.envelope as unknown as DailyEditionEnvelope;
  const envelope = await withoutTombstonedDailyItems(
    transaction.objectStore("itemTombstones"),
    userId,
    cachedEnvelope,
  );
  if (!validDaily(envelope as unknown as Record<string, unknown>)) {
    await transaction.objectStore("daily").delete(key);
    await transaction.done;
    return undefined;
  }
  if (envelope !== cachedEnvelope) {
    await transaction.objectStore("daily").put({ key, envelope, cachedAt: record.cachedAt as string });
  }
  await transaction.done;
  return envelope;
}

function dailyEnvelopeMatchesKey(envelope: { issue_date?: unknown; context_bucket?: unknown }, key: string): boolean {
  return `${envelope.issue_date}:${envelope.context_bucket}` === key;
}

export async function isItemDeleted(userId: string, itemId: string): Promise<boolean> {
  if (!userId || !validCanonicalItemId(itemId)) return false;
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "itemTombstones"], "readonly");
  const belongs = sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId);
  const tombstone = belongs ? await transaction.objectStore("itemTombstones").get(itemId) : undefined;
  await transaction.done;
  return tombstoneFences(tombstone, userId, itemId);
}

export async function deleteCachedItem(userId: string, itemId: string): Promise<void> {
  if (!userId || !validCanonicalItemId(itemId)) throw new TypeError("invalid_item_identity");
  const database = await dbPromise;
  const stores = ["meta", "articles", "annotations", "outbox", "lists", "daily", "artwork", "itemTombstones"] as const;
  const transaction = database.transaction(stores, "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.done;
    return;
  }

  const [article, outboxCount, listCount, dailyCount] = await Promise.all([
    transaction.objectStore("articles").get(itemId),
    transaction.objectStore("outbox").count(),
    transaction.objectStore("lists").count(),
    transaction.objectStore("daily").count(),
  ]);
  const outboxOverflow = outboxCount > MAX_PROGRESS_RECORDS + MAX_SESSION_RECORDS + MAX_MUTATION_RECORDS;
  const listOverflow = listCount > MAX_DELETION_LIST_RECORDS;
  const dailyOverflow = dailyCount > MAX_DELETION_DAILY_RECORDS;
  const artworkOverflowCleanup = listOverflow || dailyOverflow;
  const outbox = outboxOverflow ? [] : await transaction.objectStore("outbox").getAll();
  const lists = listOverflow ? [] : await transaction.objectStore("lists").getAll();
  const dailyRecords = dailyOverflow ? [] : await transaction.objectStore("daily").getAll();
  if (outboxOverflow) await transaction.objectStore("outbox").clear();
  if (listOverflow) await transaction.objectStore("lists").clear();
  if (dailyOverflow) await transaction.objectStore("daily").clear();
  const artworkPaths = new Set<string>();
  collectItemArtworkPaths(article?.item, itemId, artworkPaths, { remaining: 4_096 });
  for (const record of lists) collectItemArtworkPaths(record.envelope, itemId, artworkPaths, { remaining: 4_096 });
  for (const record of dailyRecords) collectItemArtworkPaths(record.envelope, itemId, artworkPaths, { remaining: 4_096 });

  await transaction.objectStore("articles").delete(itemId);
  await transaction.objectStore("annotations").delete(itemId);
  for (const record of outbox) if (record.itemId === itemId) await transaction.objectStore("outbox").delete(record.key);
  for (const record of lists) {
    if (!validListForCacheKey(record.key, record.envelope as unknown as Record<string, unknown>)) {
      await transaction.objectStore("lists").delete(record.key);
      continue;
    }
    const envelope = {
      ...record.envelope,
      items: record.envelope.items.filter((item) => item.id !== itemId),
      ...(record.envelope.results ? { results: record.envelope.results.filter((item) => item.id !== itemId) } : {}),
    };
    await transaction.objectStore("lists").put({ ...record, envelope });
  }
  for (const record of dailyRecords) {
    const envelope = removeItemFromDaily(record.envelope, itemId);
    if (validDaily(envelope as unknown as Record<string, unknown>)) await transaction.objectStore("daily").put({ ...record, envelope });
    else await transaction.objectStore("daily").delete(record.key);
  }
  if (artworkOverflowCleanup) {
    await transaction.objectStore("artwork").clear();
    await transaction.objectStore("meta").delete(ARTWORK_CACHE_INDEX_KEY);
  } else {
    for (const path of artworkPaths) await transaction.objectStore("artwork").delete(path);
    await removeArtworkCacheEntries(transaction.objectStore("meta"), artworkPaths);
  }
  await transaction.objectStore("itemTombstones").put({
    key: itemId,
    userId,
    itemId,
    deletedAt: new Date().toISOString(),
  });
  await transaction.done;
}

function collectItemArtworkPaths(value: unknown, itemId: string, paths: Set<string>, budget: { remaining: number }): void {
  const stack: unknown[] = [value];
  const seen = new WeakSet<object>();
  while (stack.length && budget.remaining > 0) {
    const current = stack.pop();
    if (!current || typeof current !== "object" || seen.has(current)) continue;
    seen.add(current);
    budget.remaining -= 1;
    if (!Array.isArray(current)) {
      const record = current as Record<string, unknown>;
      if (record.id === itemId || record.item_id === itemId) {
        const artwork = isJsonObject(record.artwork) ? record.artwork : undefined;
        if (validArtworkPath(artwork?.color_path, "color")) paths.add(artwork.color_path);
        if (validArtworkPath(artwork?.eink_path, "eink")) paths.add(artwork.eink_path);
      }
    }
    for (const key in current) {
      if (stack.length >= budget.remaining) break;
      if (!Object.prototype.hasOwnProperty.call(current, key)) continue;
      const child = (current as unknown as Record<string, unknown>)[key];
      if (child && typeof child === "object" && !seen.has(child)) stack.push(child);
    }
  }
}

function pendingItemUpdates(records: OutboxRecord[]): Map<string, Partial<Pick<ReaderItem, "status" | "is_favorite" | "reading_progress">>> {
  const updates = new Map<string, Partial<Pick<ReaderItem, "status" | "is_favorite" | "reading_progress">>>();
  for (const record of records) {
    if (record.kind !== "state" && record.kind !== "progress") continue;
    const update = updates.get(record.itemId) || {};
    if (record.kind === "progress") update.reading_progress = (record.payload as { reading_progress: number }).reading_progress;
    else {
      const state = record.payload as { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean };
      if (state.status !== undefined) update.status = state.status;
      if (state.is_favorite !== undefined) update.is_favorite = state.is_favorite;
    }
    updates.set(record.itemId, update);
  }
  return updates;
}

/** Fresh summaries must not roll back changes still waiting in this owner's outbox. */
export async function withPendingItemChanges(userId: string, items: ReaderItem[]): Promise<ReaderItem[]> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "outbox"], "readwrite");
  const owner = await transaction.objectStore("meta").get("session");
  if (!sessionBelongsTo(owner, userId)) { await transaction.done; return owner ? [] : items; }
  const pending = pendingItemUpdates(await validOutboxRecords(transaction.objectStore("outbox"), userId));
  await transaction.done;
  return items.map((item) => pending.has(item.id) ? { ...item, ...pending.get(item.id) } : item);
}

export async function cacheArticle(userId: string, item: ReaderItem): Promise<void> {
  if (!validReaderItem(item)) throw new TypeError("invalid_article_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "articles", "outbox", "itemTombstones"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)
    && !tombstoneFences(await transaction.objectStore("itemTombstones").get(item.id), userId, item.id)) {
    const pending = pendingItemUpdates(await validOutboxRecords(transaction.objectStore("outbox"), userId)).get(item.id);
    await transaction.objectStore("articles").put({ id: item.id, item: { ...item, ...pending }, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedArticle(userId: string, id: string): Promise<ReaderItem | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "articles", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  if (tombstoneFences(await transaction.objectStore("itemTombstones").get(id), userId, id)) {
    await transaction.objectStore("articles").delete(id);
    await transaction.done;
    return undefined;
  }
  const record: unknown = await transaction.objectStore("articles").get(id);
  if (!isJsonObject(record) || record.id !== id || !validCachedAt(record.cachedAt) || !validReaderItem(record.item) || record.item.id !== id) {
    if (record !== undefined) await transaction.objectStore("articles").delete(id);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.item;
}

export async function cacheInboxBriefs(userId: string, key: string, envelope: InboxBriefEnvelope): Promise<void> {
  if (!validInboxBriefEnvelope(envelope as unknown as Record<string, unknown>)) throw new TypeError("invalid_inbox_briefs_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "inboxBriefs"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.objectStore("inboxBriefs").put({ key, envelope, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedInboxBriefs(userId: string, key: string): Promise<InboxBriefEnvelope | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "inboxBriefs"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("inboxBriefs").get(key);
  if (!isJsonObject(record) || record.key !== key || !validCachedAt(record.cachedAt) || !isJsonObject(record.envelope) || !validInboxBriefEnvelope(record.envelope)) {
    if (record !== undefined) await transaction.objectStore("inboxBriefs").delete(key);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.envelope as unknown as InboxBriefEnvelope;
}

export async function cacheInboxBrief(userId: string, brief: InboxBrief): Promise<void> {
  if (!validInboxBrief(brief)) throw new TypeError("invalid_inbox_brief_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "inboxBriefDetails"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.objectStore("inboxBriefDetails").put({ id: brief.id, brief, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedInboxBrief(userId: string, id: string): Promise<InboxBrief | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "inboxBriefDetails"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("inboxBriefDetails").get(id);
  if (!isJsonObject(record) || record.id !== id || !validCachedAt(record.cachedAt) || !validInboxBrief(record.brief) || record.brief.id !== id) {
    if (record !== undefined) await transaction.objectStore("inboxBriefDetails").delete(id);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.brief;
}

export async function cachePodcastEpisodes(userId: string, key: string, envelope: PodcastEpisodeEnvelope): Promise<void> {
  if (!validPodcastEpisodeEnvelope(envelope as unknown as Record<string, unknown>)) throw new TypeError("invalid_podcast_episodes_cache");
  const safeEnvelope = { ...envelope, episodes: envelope.episodes.map((episode) => podcastReceiptForCache(episode, false)) };
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "podcastEpisodes"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) {
    await transaction.objectStore("podcastEpisodes").put({ key, envelope: safeEnvelope, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedPodcastEpisodes(userId: string, key: string): Promise<PodcastEpisodeEnvelope | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "podcastEpisodes"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("podcastEpisodes").get(key);
  if (!isJsonObject(record) || record.key !== key || !validCachedAt(record.cachedAt) || !isJsonObject(record.envelope) || !validPodcastEpisodeEnvelope(record.envelope)) {
    if (record !== undefined) await transaction.objectStore("podcastEpisodes").delete(key);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.envelope as unknown as PodcastEpisodeEnvelope;
}

export async function cachePodcastEpisode(userId: string, episode: PodcastEpisode): Promise<void> {
  if (!validPodcastEpisode(episode)) throw new TypeError("invalid_podcast_episode_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "podcastEpisodeDetails"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) await transaction.objectStore("podcastEpisodeDetails").put({
    id: episode.id,
    episode: podcastReceiptForCache(episode, true),
    cachedAt: new Date().toISOString(),
  });
  await transaction.done;
}

export async function cachedPodcastEpisode(userId: string, id: string): Promise<PodcastEpisode | undefined> {
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "podcastEpisodeDetails"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const record: unknown = await transaction.objectStore("podcastEpisodeDetails").get(id);
  if (!isJsonObject(record) || record.id !== id || !validCachedAt(record.cachedAt) || !validPodcastEpisode(record.episode) || record.episode.id !== id) {
    if (record !== undefined) await transaction.objectStore("podcastEpisodeDetails").delete(id);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return record.episode;
}

function podcastReceiptForCache(episode: PodcastEpisode, includeScript: boolean): PodcastEpisode {
  const receipt = pickFields(episode, [
    "id", "issue_date", "title", "subtitle", "status", "state", "source_count", "covered_count", "read_original_count",
    "created_at", "published_at", "archived_at", "artwork_state",
  ] as const) as PodcastEpisode;
  receipt.variants = (episode.variants || []).map((variant) => pickFields(variant, [
    "id", "voice", "tts_model", "state", "audio_sha256", "byte_length", "duration_seconds", "chunk_count", "completed_at",
  ] as const));
  if (episode.artwork) receipt.artwork = artworkForCache(episode.artwork);
  else if (episode.artwork === null) receipt.artwork = null;
  if (episode.chapters) {
    receipt.chapters = episode.chapters.map((chapter) => ({
      ...pickFields(chapter, ["id", "title", "position", "start_seconds", "artwork_state"] as const),
      ...(chapter.artwork ? { artwork: artworkForCache(chapter.artwork) } : chapter.artwork === null ? { artwork: null } : {}),
    }));
  }
  if (episode.sources) {
    receipt.sources = episode.sources.map((source) => pickFields(source, [
      "item_id", "item_version", "title", "site_name", "takeaway", "editorial_role", "original_status", "state", "default_selected", "state_updated_at",
    ] as const));
  }
  if (episode.actions) {
    receipt.actions = episode.actions.map((action) => pickFields(action, [
      "item_id", "action", "from_item_status", "to_item_status", "created_at",
    ] as const));
  }
  if (includeScript && typeof episode.script_markdown === "string") receipt.script_markdown = episode.script_markdown;
  return receipt;
}

function artworkForCache(artwork: NonNullable<PodcastEpisode["artwork"]>): NonNullable<PodcastEpisode["artwork"]> {
  return pickFields(artwork, [
    "id", "version", "aspect_ratio", "focal_x", "focal_y", "color_path", "eink_path", "pixel_width", "pixel_height",
  ] as const);
}

function pickFields<T extends object, K extends readonly (keyof T)[]>(value: T, keys: K): Pick<T, K[number]> {
  const picked = {} as Pick<T, K[number]>;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(value, key)) picked[key] = value[key];
  }
  return picked;
}

export async function artworkSessionIsCurrent(
  userId: string,
  generation: string,
  ownerItemId?: string,
): Promise<boolean> {
  if (!isCanonicalUserId(userId)
    || !validSessionGeneration(generation)
    || (ownerItemId !== undefined && !validCanonicalItemId(ownerItemId))) return false;
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "itemTombstones"], "readonly");
  const belongs = sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId, generation);
  const deleted = belongs && ownerItemId
    ? tombstoneFences(await transaction.objectStore("itemTombstones").get(ownerItemId), userId, ownerItemId)
    : false;
  await transaction.done;
  return belongs && !deleted;
}

export async function cachedArtwork(
  userId: string,
  path: string,
  ownerItemId?: string,
  sessionGeneration?: string,
): Promise<Blob | undefined> {
  if (!validArtworkCachePath(path)
    || (ownerItemId !== undefined && !validCanonicalItemId(ownerItemId))
    || (sessionGeneration !== undefined && !validSessionGeneration(sessionGeneration))) return undefined;
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "artwork", "itemTombstones"], "readonly");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId, sessionGeneration)) { await transaction.done; return undefined; }
  if (ownerItemId && tombstoneFences(await transaction.objectStore("itemTombstones").get(ownerItemId), userId, ownerItemId)) {
    await transaction.done;
    return undefined;
  }
  const store = transaction.objectStore("artwork");
  const record: unknown = await store.get(path);
  await transaction.done;
  if (!isValidArtworkRecord(record, path) || !(await validArtworkBlob(record.blob))) {
    if (record !== undefined) await deleteArtworkIfUnchanged(database, userId, path, record, sessionGeneration);
    return undefined;
  }
  const touch = database.transaction(["meta", "artwork", "itemTombstones"], "readwrite");
  const meta = touch.objectStore("meta");
  const artworkStore = touch.objectStore("artwork");
  const belongs = sessionBelongsTo(await meta.get("session"), userId, sessionGeneration);
  const deleted = ownerItemId
    ? tombstoneFences(await touch.objectStore("itemTombstones").get(ownerItemId), userId, ownerItemId)
    : false;
  if (belongs && !deleted) {
    const current = await artworkStore.get(path);
    if (isValidArtworkRecord(current, path) && current.lastAccessedAt === record.lastAccessedAt && current.byteCount === record.byteCount) {
      const lastAccessedAt = new Date().toISOString();
      await artworkStore.put({ ...current, lastAccessedAt });
      await touchArtworkCacheEntry(meta, artworkStore, current, lastAccessedAt);
    }
  }
  await touch.done;
  if (!belongs || deleted) return undefined;
  return record.blob;
}

export async function cacheArtwork(
  userId: string,
  path: string,
  blob: Blob,
  ownerItemId?: string,
  sessionGeneration?: string,
): Promise<boolean> {
  if (!validArtworkCachePath(path)
    || (ownerItemId !== undefined && !validCanonicalItemId(ownerItemId))
    || (sessionGeneration !== undefined && !validSessionGeneration(sessionGeneration))
    || !isBlobLike(blob)
    || !blob.size
    || blob.size > MAX_ARTWORK_CACHE_BYTES
    || !(await validArtworkBlob(blob, true))) return false;
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "artwork", "itemTombstones"], "readwrite");
  const meta = transaction.objectStore("meta");
  if (!sessionBelongsTo(await meta.get("session"), userId, sessionGeneration)) { await transaction.done; return false; }
  if (ownerItemId && tombstoneFences(await transaction.objectStore("itemTombstones").get(ownerItemId), userId, ownerItemId)) {
    await transaction.done;
    return false;
  }
  const store = transaction.objectStore("artwork");
  const entries = await validArtworkCacheEntries(meta, store);
  const retained = entries
    .filter((record) => record.path !== path)
    .sort((left, right) => left.lastAccessedAt.localeCompare(right.lastAccessedAt) || left.path.localeCompare(right.path));
  let total = retained.reduce((sum, record) => sum + record.byteCount, 0) + blob.size;
  while (retained.length && (total > MAX_ARTWORK_CACHE_BYTES || retained.length >= MAX_ARTWORK_RECORDS)) {
    const record = retained.shift() as ArtworkCacheEntry;
    await store.delete(record.path);
    total -= record.byteCount;
  }
  const lastAccessedAt = new Date().toISOString();
  await store.put({
    path,
    blob,
    byteCount: blob.size,
    lastAccessedAt,
  });
  await meta.put(artworkCacheIndex([...retained, { path, byteCount: blob.size, lastAccessedAt }]));
  await transaction.done;
  return true;
}

export async function deleteCachedArtwork(userId: string, path: string, sessionGeneration?: string): Promise<void> {
  if (!isCanonicalUserId(userId)
    || !validArtworkCachePath(path)
    || (sessionGeneration !== undefined && !validSessionGeneration(sessionGeneration))) return;
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "artwork"], "readwrite");
  const meta = transaction.objectStore("meta");
  if (sessionBelongsTo(await meta.get("session"), userId, sessionGeneration)) {
    await transaction.objectStore("artwork").delete(path);
    await removeArtworkCacheEntries(meta, new Set([path]));
  }
  await transaction.done;
}

function isValidArtworkRecord(value: unknown, expectedPath?: string): value is ArtworkRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Partial<ArtworkRecord>;
  return typeof record.path === "string"
    && Boolean(record.path)
    && (expectedPath === undefined || record.path === expectedPath)
    && isBlobLike(record.blob)
    && record.blob.size > 0
    && record.blob.size <= MAX_ARTWORK_CACHE_BYTES
    && (record.blob.type === "image/png" || record.blob.type === "image/jpeg")
    && Number.isSafeInteger(record.byteCount)
    && record.byteCount === record.blob.size
    && typeof record.lastAccessedAt === "string"
    && Number.isFinite(Date.parse(record.lastAccessedAt));
}

function isBlobLike(value: unknown): value is Blob {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const blob = value as Partial<Blob>;
  return typeof blob.size === "number"
    && Number.isFinite(blob.size)
    && typeof blob.type === "string"
    && typeof blob.slice === "function"
    && typeof blob.arrayBuffer === "function";
}

export async function validArtworkBlob(blob: Blob, decode = false): Promise<boolean> {
  if (!isBlobLike(blob)) return false;
  try {
    const dimensions = blob.type === "image/png"
      ? await pngDimensions(blob)
      : blob.type === "image/jpeg" ? await jpegBlobDimensions(blob) : undefined;
    if (!dimensions || !imageDimensionsAreSafe(dimensions.width, dimensions.height)) return false;
    if (!decode || typeof createImageBitmap !== "function") return true;
    const bitmap = await createImageBitmap(blob);
    try { return imageDimensionsAreSafe(bitmap.width, bitmap.height); }
    finally { bitmap.close(); }
  } catch {
    return false;
  }
}

function imageDimensionsAreSafe(width: number, height: number): boolean {
  return Number.isSafeInteger(width)
    && Number.isSafeInteger(height)
    && width > 0
    && height > 0
    && width <= MAX_ARTWORK_DIMENSION
    && height <= MAX_ARTWORK_DIMENSION
    && height <= Math.floor(MAX_ARTWORK_PIXELS / width);
}

async function pngDimensions(blob: Blob): Promise<{ width: number; height: number } | undefined> {
  if (blob.size < 57) return undefined;
  const bytes = new Uint8Array(await blob.slice(0, 33).arrayBuffer());
  const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (!signature.every((byte, index) => bytes[index] === byte)) return undefined;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(8) !== 13
    || String.fromCharCode(...bytes.slice(12, 16)) !== "IHDR"
    || pngCrc32(bytes.subarray(12, 29)) !== view.getUint32(29)) return undefined;
  const dimensions = { width: view.getUint32(16), height: view.getUint32(20) };
  if (!imageDimensionsAreSafe(dimensions.width, dimensions.height)
    || !validPngColorDepth(bytes[24], bytes[25])
    || bytes[26] !== 0
    || bytes[27] !== 0
    || (bytes[28] !== 0 && bytes[28] !== 1)) return undefined;

  const indexedColor = bytes[25] === 3;
  let offset = 33;
  let chunkCount = 1;
  let sawPalette = false;
  let sawImageData = false;
  let imageDataEnded = false;
  let imageDataBytes = 0;
  while (offset + 12 <= blob.size && chunkCount < MAX_PNG_CHUNKS) {
    const header = new Uint8Array(await blob.slice(offset, offset + 8).arrayBuffer());
    const headerView = new DataView(header.buffer, header.byteOffset, header.byteLength);
    const length = headerView.getUint32(0);
    if (length > blob.size - offset - 12 || !header.slice(4, 8).every(isAsciiLetter)) return undefined;
    const type = String.fromCharCode(...header.slice(4, 8));
    const chunkEnd = offset + 12 + length;
    const chunk = new Uint8Array(await blob.slice(offset + 4, chunkEnd).arrayBuffer());
    if (chunk.byteLength !== length + 8) return undefined;
    const chunkView = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength);
    if (pngCrc32(chunk.subarray(0, length + 4)) !== chunkView.getUint32(length + 4)) return undefined;
    if (type === "IEND") {
      return length === 0
        && chunkEnd === blob.size
        && sawImageData
        && imageDataBytes > 0
        && (!indexedColor || sawPalette)
        ? dimensions
        : undefined;
    }
    if (type === "IHDR") return undefined;
    if (type === "PLTE") {
      if (sawPalette || sawImageData || length < 3 || length > 768 || length % 3 !== 0) return undefined;
      sawPalette = true;
    } else if (type === "IDAT") {
      if (imageDataEnded) return undefined;
      sawImageData = true;
      imageDataBytes += length;
    } else {
      if (sawImageData) imageDataEnded = true;
      if ((header[4] & 0x20) === 0) return undefined;
    }
    offset = chunkEnd;
    chunkCount += 1;
  }
  return undefined;
}

function validPngColorDepth(bitDepth: number, colorType: number): boolean {
  if (colorType === 0) return [1, 2, 4, 8, 16].includes(bitDepth);
  if (colorType === 2 || colorType === 4 || colorType === 6) return bitDepth === 8 || bitDepth === 16;
  return colorType === 3 && [1, 2, 4, 8].includes(bitDepth);
}

function isAsciiLetter(byte: number): boolean {
  return (byte >= 0x41 && byte <= 0x5a) || (byte >= 0x61 && byte <= 0x7a);
}

function pngCrc32(bytes: Uint8Array): number {
  let crc = 0xffff_ffff;
  for (const byte of bytes) crc = PNG_CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffff_ffff) >>> 0;
}

async function jpegBlobDimensions(blob: Blob): Promise<{ width: number; height: number } | undefined> {
  if (blob.size < 16) return undefined;
  const [header, tail] = await Promise.all([
    blob.slice(0, Math.min(blob.size, MAX_JPEG_HEADER_BYTES)).arrayBuffer(),
    blob.slice(-2).arrayBuffer(),
  ]);
  const last = new Uint8Array(tail);
  if (last[0] !== 0xff || last[1] !== 0xd9) return undefined;
  return jpegDimensions(new Uint8Array(header), blob.size);
}

function jpegDimensions(bytes: Uint8Array, totalSize: number): { width: number; height: number } | undefined {
  if (bytes.length < 12 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return undefined;
  let offset = 2;
  let dimensions: { width: number; height: number } | undefined;
  let sawQuantizationTable = false;
  let sawEntropyTable = false;
  while (offset < bytes.length) {
    if (bytes[offset] !== 0xff) return undefined;
    while (offset < bytes.length && bytes[offset] === 0xff) offset += 1;
    if (offset >= bytes.length) return undefined;
    const marker = bytes[offset];
    offset += 1;
    if (marker === 0x00) return undefined;
    if (marker === 0xd9 || marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) return undefined;
    if (offset + 2 > bytes.length) return undefined;
    const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
    if (segmentLength < 2 || offset + segmentLength > bytes.length) return undefined;
    if (isJpegStartOfFrame(marker)) {
      const componentCount = bytes[offset + 7];
      if (dimensions || componentCount < 1 || componentCount > 4 || segmentLength !== 8 + (3 * componentCount)) return undefined;
      dimensions = {
        height: (bytes[offset + 3] << 8) | bytes[offset + 4],
        width: (bytes[offset + 5] << 8) | bytes[offset + 6],
      };
      if (!imageDimensionsAreSafe(dimensions.width, dimensions.height)) return undefined;
    } else if (marker === 0xdb) {
      sawQuantizationTable = segmentLength >= 67;
    } else if (marker === 0xc4 || marker === 0xcc) {
      sawEntropyTable = segmentLength >= 4;
    } else if (marker === 0xda) {
      const componentCount = bytes[offset + 2];
      return dimensions
        && sawQuantizationTable
        && sawEntropyTable
        && componentCount >= 1
        && componentCount <= 4
        && segmentLength === 6 + (2 * componentCount)
        && offset + segmentLength < bytes.length
        && offset + segmentLength < totalSize - 2
        ? dimensions
        : undefined;
    }
    offset += segmentLength;
  }
  return undefined;
}

function isJpegStartOfFrame(marker: number): boolean {
  return (marker >= 0xc0 && marker <= 0xc3)
    || (marker >= 0xc5 && marker <= 0xc7)
    || (marker >= 0xc9 && marker <= 0xcb)
    || (marker >= 0xcd && marker <= 0xcf);
}

async function deleteArtworkIfUnchanged(
  database: Awaited<typeof dbPromise>,
  userId: string,
  path: string,
  rejected: unknown,
  sessionGeneration?: string,
): Promise<void> {
  const transaction = database.transaction(["meta", "artwork", "itemTombstones"], "readwrite");
  const meta = transaction.objectStore("meta");
  if (sessionBelongsTo(await meta.get("session"), userId, sessionGeneration)) {
    const current: unknown = await transaction.objectStore("artwork").get(path);
    if (isJsonObject(current) && isJsonObject(rejected)
      && current.path === rejected.path
      && current.byteCount === rejected.byteCount
      && current.lastAccessedAt === rejected.lastAccessedAt) {
      await transaction.objectStore("artwork").delete(path);
      await removeArtworkCacheEntries(meta, new Set([path]));
    }
  }
  await transaction.done;
}

type ArtworkStore = IDBPObjectStore<ImprintDB, ["meta", "artwork", "itemTombstones"], "artwork", "readwrite">;
type ArtworkMetaStore = {
  get(key: string): Promise<MetaRecord | undefined>;
  put(value: MetaRecord): Promise<IDBValidKey>;
  delete(key: string): Promise<void>;
};

async function validArtworkCacheEntries(meta: ArtworkMetaStore, store: ArtworkStore): Promise<ArtworkCacheEntry[]> {
  const count = await store.count();
  if (count > MAX_ARTWORK_RECORDS) {
    await store.clear();
    await meta.delete(ARTWORK_CACHE_INDEX_KEY);
    return [];
  }
  const [record, keys] = await Promise.all([
    meta.get(ARTWORK_CACHE_INDEX_KEY),
    store.getAllKeys(),
  ]);
  const entries = artworkCacheEntries(record);
  if (!entries || !artworkCacheIndexMatchesKeys(entries, keys)) {
    // Legacy or corrupt caches have no trustworthy blob-free byte ledger. Clear them once
    // instead of cloning every private Blob or making eviction decisions from guessed size.
    if (count) await store.clear();
    await meta.delete(ARTWORK_CACHE_INDEX_KEY);
    return [];
  }
  return entries;
}

function artworkCacheEntries(value: unknown): ArtworkCacheEntry[] | undefined {
  if (!isJsonObject(value)
    || value.key !== ARTWORK_CACHE_INDEX_KEY
    || value.version !== 1
    || !Array.isArray(value.entries)
    || value.entries.length > MAX_ARTWORK_RECORDS) return undefined;
  const entries: ArtworkCacheEntry[] = [];
  const paths = new Set<string>();
  let total = 0;
  for (const entry of value.entries) {
    if (!isValidArtworkCacheEntry(entry) || paths.has(entry.path)) return undefined;
    paths.add(entry.path);
    total += entry.byteCount;
    if (!Number.isSafeInteger(total) || total > MAX_ARTWORK_CACHE_BYTES) return undefined;
    entries.push(entry);
  }
  return entries;
}

function isValidArtworkCacheEntry(value: unknown): value is ArtworkCacheEntry {
  if (!isJsonObject(value)) return false;
  return validArtworkCachePath(value.path)
    && Number.isSafeInteger(value.byteCount)
    && (value.byteCount as number) > 0
    && (value.byteCount as number) <= MAX_ARTWORK_CACHE_BYTES
    && typeof value.lastAccessedAt === "string"
    && Number.isFinite(Date.parse(value.lastAccessedAt));
}

function validArtworkCachePath(value: unknown): value is string {
  return validArtworkPath(value, "color") || validArtworkPath(value, "eink");
}

function artworkCacheIndexMatchesKeys(entries: ArtworkCacheEntry[], keys: IDBValidKey[]): boolean {
  if (entries.length !== keys.length) return false;
  const paths = new Set(entries.map((entry) => entry.path));
  return keys.every((key) => typeof key === "string" && paths.has(key));
}

function artworkCacheIndex(entries: ArtworkCacheEntry[]): ArtworkCacheIndexRecord {
  return { key: ARTWORK_CACHE_INDEX_KEY, version: 1, entries };
}

async function touchArtworkCacheEntry(
  meta: ArtworkMetaStore,
  store: ArtworkStore,
  current: ArtworkRecord,
  lastAccessedAt: string,
): Promise<void> {
  const entries = artworkCacheEntries(await meta.get(ARTWORK_CACHE_INDEX_KEY));
  const matchingIndex = entries?.findIndex((entry) => entry.path === current.path
    && entry.byteCount === current.byteCount
    && entry.lastAccessedAt === current.lastAccessedAt) ?? -1;
  if (entries && matchingIndex >= 0) {
    entries[matchingIndex] = { path: current.path, byteCount: current.byteCount, lastAccessedAt };
    await meta.put(artworkCacheIndex(entries));
    return;
  }
  if (await store.count() === 1) {
    await meta.put(artworkCacheIndex([{ path: current.path, byteCount: current.byteCount, lastAccessedAt }]));
    return;
  }
  await meta.delete(ARTWORK_CACHE_INDEX_KEY);
}

async function removeArtworkCacheEntries(
  meta: ArtworkMetaStore,
  paths: Set<string>,
): Promise<void> {
  const entries = artworkCacheEntries(await meta.get(ARTWORK_CACHE_INDEX_KEY));
  if (!entries) {
    await meta.delete(ARTWORK_CACHE_INDEX_KEY);
    return;
  }
  const retained = entries.filter((entry) => !paths.has(entry.path));
  if (retained.length === entries.length) return;
  if (retained.length) await meta.put(artworkCacheIndex(retained));
  else await meta.delete(ARTWORK_CACHE_INDEX_KEY);
}

export async function cacheAnnotations(userId: string, itemId: string, envelope: AnnotationEnvelope): Promise<void> {
  if (!validCanonicalItemId(itemId)
    || !validAnnotationEnvelope(envelope as unknown as Record<string, unknown>)
    || envelope.annotations.some((annotation) => annotation.item_id !== itemId)) throw new TypeError("invalid_annotations_cache");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "annotations", "outbox", "itemTombstones"], "readwrite");
  if (sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)
    && !tombstoneFences(await transaction.objectStore("itemTombstones").get(itemId), userId, itemId)) {
    const notes = new Map((await validOutboxRecords(transaction.objectStore("outbox"), userId))
      .filter((record) => record.kind === "annotation" && record.itemId === itemId)
      .map((record) => { const payload = record.payload as { annotation_id: string; note: string }; return [payload.annotation_id, payload.note]; }));
    const merged = { ...envelope, annotations: envelope.annotations.map((annotation) => notes.has(annotation.id) ? { ...annotation, note: notes.get(annotation.id)! } : annotation) };
    await transaction.objectStore("annotations").put({ itemId, envelope: merged, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function cachedAnnotations(userId: string, itemId: string): Promise<AnnotationEnvelope | undefined> {
  if (!validCanonicalItemId(itemId)) return undefined;
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "annotations", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  if (tombstoneFences(await transaction.objectStore("itemTombstones").get(itemId), userId, itemId)) {
    await transaction.objectStore("annotations").delete(itemId);
    await transaction.done;
    return undefined;
  }
  const record: unknown = await transaction.objectStore("annotations").get(itemId);
  const envelope = isJsonObject(record) && isJsonObject(record.envelope) ? record.envelope as unknown as AnnotationEnvelope : undefined;
  if (!isJsonObject(record) || record.itemId !== itemId || !validCachedAt(record.cachedAt) || !isJsonObject(record.envelope) || !validAnnotationEnvelope(record.envelope)
    || !envelope || envelope.annotations.some((annotation) => annotation.item_id !== itemId)) {
    if (record !== undefined) await transaction.objectStore("annotations").delete(itemId);
    await transaction.done;
    return undefined;
  }
  await transaction.done;
  return envelope;
}

export async function queueProgress(userId: string, itemId: string, progress: number): Promise<void> {
  if (!validCanonicalItemId(itemId) || !Number.isFinite(progress) || progress < 0 || progress > 1) throw new TypeError("invalid_progress");
  const database = await dbPromise;
  const key = `progress:${itemId}`;
  const transaction = database.transaction(["meta", "outbox", "catalog", "articles", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return; }
  if (tombstoneFences(await transaction.objectStore("itemTombstones").get(itemId), userId, itemId)) {
    await transaction.objectStore("outbox").delete(key);
    await transaction.done;
    return;
  }
  const store = transaction.objectStore("outbox");
  const records = await validOutboxRecords(store, userId);
  const existing = records.find((record) => record.key === key);
  if (!existing) {
    const progressRecords = records.filter((record) => record.kind === "progress").sort(compareOutboxRecords);
    for (const record of progressRecords.slice(0, Math.max(0, progressRecords.length - (MAX_PROGRESS_RECORDS - 1)))) await store.delete(record.key);
  }
  const now = Date.now();
  const inheritedLease = existing && hasLiveLease(existing, now)
    ? { leaseOwner: existing.leaseOwner, leaseExpiresAt: existing.leaseExpiresAt }
    : {};
  await store.put({
    key,
    version: crypto.randomUUID(),
    userId,
    kind: "progress",
    itemId,
    payload: { reading_progress: progress },
    createdAt: new Date(now).toISOString(),
    ...inheritedLease,
  });
  const catalog = await transaction.objectStore("catalog").get(itemId);
  if (catalog) await transaction.objectStore("catalog").put({ ...catalog, item: { ...catalog.item, reading_progress: progress } });
  const article = await transaction.objectStore("articles").get(itemId);
  if (article) await transaction.objectStore("articles").put({ ...article, item: { ...article.item, reading_progress: progress } });
  await transaction.done;
}

export async function queueReadingSession(userId: string, session: ReadingSessionInput): Promise<void> {
  if (!isValidReadingSession(session)) throw new TypeError("invalid_reading_session");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "outbox", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return; }
  if (tombstoneFences(await transaction.objectStore("itemTombstones").get(session.item_id), userId, session.item_id)) {
    await transaction.done;
    return;
  }
  const store = transaction.objectStore("outbox");
  const sessions = (await validOutboxRecords(store, userId)).filter((record) => record.kind === "session").sort(compareOutboxRecords);
  for (const record of sessions.slice(0, Math.max(0, sessions.length - (MAX_SESSION_RECORDS - 1)))) await store.delete(record.key);
  const version = crypto.randomUUID();
  await store.put({
    key: `session:${version}`,
    version,
    userId,
    kind: "session",
    itemId: session.item_id,
    payload: session,
    createdAt: new Date().toISOString(),
  });
  await transaction.done;
}

export async function cacheConfirmedItemState(userId: string, itemId: string, state: { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean }): Promise<void> {
  if (!validCanonicalItemId(itemId)) throw new TypeError("invalid_item_state");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "outbox", "catalog", "articles", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)
    || tombstoneFences(await transaction.objectStore("itemTombstones").get(itemId), userId, itemId)) { await transaction.done; return; }
  const pending = pendingItemUpdates(await validOutboxRecords(transaction.objectStore("outbox"), userId)).get(itemId);
  // A server acknowledgement must not erase newer unsynced intent or replace
  // the article body with the summary returned by the item-state endpoint.
  for (const name of ["catalog", "articles"] as const) {
    const store = transaction.objectStore(name);
    const record = await store.get(itemId);
    if (record) await store.put({ ...record, item: { ...record.item, ...state, ...pending }, cachedAt: new Date().toISOString() });
  }
  await transaction.done;
}

export async function queueItemState(userId: string, itemId: string, state: { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean }): Promise<void> {
  if (!validCanonicalItemId(itemId) || (!state.status && state.is_favorite === undefined)) throw new TypeError("invalid_item_state");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "outbox", "catalog", "articles", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return; }
  if (tombstoneFences(await transaction.objectStore("itemTombstones").get(itemId), userId, itemId)) { await transaction.done; return; }
  const store = transaction.objectStore("outbox");
  const key = `state:${itemId}`;
  const records = await validOutboxRecords(store, userId);
  const existing = records.find((record) => record.key === key && record.kind === "state");
  const mutationRecords = records.filter((record) => record.kind === "state" || record.kind === "annotation").sort(compareOutboxRecords);
  if (!existing && mutationRecords.length >= MAX_MUTATION_RECORDS) {
    await transaction.done;
    throw new Error("offline_mutation_queue_full");
  }
  const payload = { ...(existing?.payload as { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean } || {}), ...state };
  await store.put({ key, version: crypto.randomUUID(), userId, kind: "state", itemId, payload, createdAt: new Date().toISOString(), ...inheritedOutboxLease(existing) });
  const catalog = await transaction.objectStore("catalog").get(itemId);
  if (catalog) await transaction.objectStore("catalog").put({ ...catalog, item: { ...catalog.item, ...state }, cachedAt: new Date().toISOString() });
  const article = await transaction.objectStore("articles").get(itemId);
  if (article) await transaction.objectStore("articles").put({ ...article, item: { ...article.item, ...state }, cachedAt: new Date().toISOString() });
  await transaction.done;
}

export async function queueAnnotationNote(userId: string, itemId: string, annotationId: string, note: string): Promise<void> {
  if (!validCanonicalItemId(itemId) || !/^ann_[a-zA-Z0-9_-]{1,128}$/.test(annotationId) || typeof note !== "string" || note.length > 10_000) throw new TypeError("invalid_annotation_note");
  const database = await dbPromise;
  const transaction = database.transaction(["meta", "outbox", "annotations", "itemTombstones"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return; }
  if (tombstoneFences(await transaction.objectStore("itemTombstones").get(itemId), userId, itemId)) { await transaction.done; return; }
  const store = transaction.objectStore("outbox");
  const key = `annotation:${annotationId}`;
  const records = await validOutboxRecords(store, userId);
  const existing = records.find((record) => record.key === key);
  const mutations = records.filter((record) => record.kind === "state" || record.kind === "annotation").sort(compareOutboxRecords);
  if (!existing && mutations.length >= MAX_MUTATION_RECORDS) {
    await transaction.done;
    throw new Error("offline_mutation_queue_full");
  }
  await store.put({ key, version: crypto.randomUUID(), userId, kind: "annotation", itemId, payload: { annotation_id: annotationId, note }, createdAt: new Date().toISOString(), ...inheritedOutboxLease(existing) });
  const annotations = await transaction.objectStore("annotations").get(itemId);
  if (annotations) await transaction.objectStore("annotations").put({ ...annotations, envelope: { ...annotations.envelope, annotations: annotations.envelope.annotations.map((annotation) => annotation.id === annotationId ? { ...annotation, note, updated_at: new Date().toISOString() } : annotation) }, cachedAt: new Date().toISOString() });
  await transaction.done;
}

type OutboxHandlers = {
  progress: (itemId: string, progress: number) => Promise<unknown>;
  session: (session: ReadingSessionInput) => Promise<unknown>;
  state?: (itemId: string, state: { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean }) => Promise<unknown>;
  annotation?: (annotationId: string, note: string) => Promise<unknown>;
};

type WithOutboxLock = (operation: () => Promise<void>) => Promise<void>;

export function createOutboxFlushCoordinator({
  ownerId = crypto.randomUUID(),
  now = () => Date.now(),
  leaseDurationMs = OUTBOX_LEASE_DURATION_MS,
  withLock = withSameOriginOutboxLock,
  withSessionLock = withSameOriginSessionTransitionLock,
}: {
  ownerId?: string;
  now?: () => number;
  leaseDurationMs?: number;
  withLock?: WithOutboxLock;
  withSessionLock?: SessionTransitionLock;
} = {}): (userId: string, handlers: OutboxHandlers) => Promise<void> {
  if (!ownerId || !Number.isFinite(leaseDurationMs) || leaseDurationMs < MIN_OUTBOX_LEASE_DURATION_MS || leaseDurationMs > MAX_OUTBOX_LEASE_DURATION_MS) {
    throw new TypeError("invalid_outbox_coordinator");
  }
  const activeFlushes = new Map<string, Promise<void>>();
  return (userId, handlers) => {
    if (!userId) return Promise.reject(new TypeError("invalid_outbox_user"));
    const active = activeFlushes.get(userId);
    if (active) return active;
    const pending = runOutboxFlush(userId, handlers, ownerId, now, leaseDurationMs, withLock, withSessionLock);
    const tracked = pending.finally(() => {
      if (activeFlushes.get(userId) === tracked) activeFlushes.delete(userId);
    });
    activeFlushes.set(userId, tracked);
    return tracked;
  };
}

export const flushOutbox = createOutboxFlushCoordinator();
export const flushOutboxWithinSessionTransition = createOutboxFlushCoordinator({
  withSessionLock: async <T>(operation: () => Promise<T>) => operation(),
});

async function withSameOriginOutboxLock(operation: () => Promise<void>): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(OUTBOX_LOCK_NAME, { mode: "exclusive" }, operation);
  }
  return operation();
}

async function runOutboxFlush(
  userId: string,
  handlers: OutboxHandlers,
  ownerId: string,
  now: () => number,
  leaseDurationMs: number,
  withLock: WithOutboxLock,
  withSessionLock: SessionTransitionLock,
): Promise<void> {
  const database = await dbPromise;
  while (true) {
    const disposition = await withSessionLock(async (): Promise<"continue" | "empty" | "stale"> => {
      let record: ClaimedOutboxRecord | undefined;
      await withLock(async () => { record = await claimNextOutboxRecord(database, userId, ownerId, now(), leaseDurationMs); });
      const claimed = record;
      if (!claimed) return "empty";
      if (!(await outboxUserIsCurrent(database, userId))) {
        await withLock(() => releaseOutboxClaim(database, claimed));
        return "stale";
      }
      try {
        if (claimed.kind === "progress") {
          await handlers.progress(claimed.itemId, (claimed.payload as { reading_progress: number }).reading_progress);
        } else if (claimed.kind === "session") {
          await handlers.session(claimed.payload as ReadingSessionInput);
        } else if (claimed.kind === "state" && handlers.state) {
          await handlers.state(claimed.itemId, claimed.payload as { status?: "inbox" | "queue" | "archive"; is_favorite?: boolean });
        } else if (claimed.kind === "annotation" && handlers.annotation) {
          const payload = claimed.payload as { annotation_id: string; note: string };
          await handlers.annotation(payload.annotation_id, payload.note);
        } else {
          throw new TypeError("missing_outbox_handler");
        }
      } catch (error) {
        if (isPermanentOutboxFailure(error)) {
          await withLock(() => completeOutboxClaim(database, claimed));
          return "continue";
        }
        await withLock(() => releaseOutboxClaim(database, claimed));
        throw error;
      }
      if (!(await outboxUserIsCurrent(database, userId))) {
        await withLock(() => releaseOutboxClaim(database, claimed));
        return "stale";
      }
      await withLock(() => completeOutboxClaim(database, claimed));
      return "continue";
    });
    if (disposition !== "continue") return;
  }
}

async function outboxUserIsCurrent(database: Awaited<typeof dbPromise>, userId: string): Promise<boolean> {
  const transaction = database.transaction("meta", "readonly");
  const belongs = sessionBelongsTo(await transaction.store.get("session"), userId);
  await transaction.done;
  return belongs;
}

export function isPermanentOutboxFailure(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("status" in error)) return false;
  const status = (error as { status?: unknown }).status;
  if (typeof status !== "number") return false;
  return Number.isInteger(status) && [400, 404, 405, 409, 410, 413, 422].includes(status);
}

async function claimNextOutboxRecord(
  database: Awaited<typeof dbPromise>,
  userId: string,
  ownerId: string,
  now: number,
  leaseDurationMs: number,
): Promise<ClaimedOutboxRecord | undefined> {
  const transaction = database.transaction(["meta", "outbox"], "readwrite");
  if (!sessionBelongsTo(await transaction.objectStore("meta").get("session"), userId)) { await transaction.done; return undefined; }
  const store = transaction.objectStore("outbox");
  const records = (await validOutboxRecords(store, userId)).sort(compareOutboxRecords);
  const record = records.find((candidate) => !hasLiveLease(candidate, now));
  if (!record) {
    await transaction.done;
    return undefined;
  }
  const claimed: ClaimedOutboxRecord = {
    ...record,
    version: record.version || crypto.randomUUID(),
    userId,
    leaseOwner: ownerId,
    leaseExpiresAt: now + leaseDurationMs,
  };
  await store.put(claimed);
  await transaction.done;
  return claimed;
}

async function completeOutboxClaim(database: Awaited<typeof dbPromise>, claimed: ClaimedOutboxRecord): Promise<void> {
  const transaction = database.transaction("outbox", "readwrite");
  const current: unknown = await transaction.store.get(claimed.key);
  if (!isValidOutboxRecord(current)) {
    if (current !== undefined) await transaction.store.delete(claimed.key);
  } else if (current.leaseOwner === claimed.leaseOwner && current.version === claimed.version) {
    await transaction.store.delete(claimed.key);
  } else if (current.leaseOwner === claimed.leaseOwner) {
    await transaction.store.put(withoutLease(current));
  }
  await transaction.done;
}

async function releaseOutboxClaim(database: Awaited<typeof dbPromise>, claimed: ClaimedOutboxRecord): Promise<void> {
  const transaction = database.transaction("outbox", "readwrite");
  const current: unknown = await transaction.store.get(claimed.key);
  if (!isValidOutboxRecord(current)) {
    if (current !== undefined) await transaction.store.delete(claimed.key);
  } else if (current.leaseOwner === claimed.leaseOwner) {
    await transaction.store.put(withoutLease(current));
  }
  await transaction.done;
}

function withoutLease(record: OutboxRecord): OutboxRecord {
  const { leaseOwner: _leaseOwner, leaseExpiresAt: _leaseExpiresAt, ...unleased } = record;
  return unleased;
}

function inheritedOutboxLease(record: OutboxRecord | undefined): Pick<OutboxRecord, "leaseOwner" | "leaseExpiresAt"> {
  return record && hasLiveLease(record, Date.now())
    ? { leaseOwner: record.leaseOwner, leaseExpiresAt: record.leaseExpiresAt }
    : {};
}

function hasLiveLease(record: OutboxRecord, now: number): boolean {
  return typeof record.leaseOwner === "string"
    && Boolean(record.leaseOwner)
    && typeof record.leaseExpiresAt === "number"
    && Number.isFinite(record.leaseExpiresAt)
    && record.leaseExpiresAt > now
    && record.leaseExpiresAt <= now + MAX_OUTBOX_LEASE_DURATION_MS;
}

async function validOutboxRecords<Stores extends ArrayLike<StoreNames<ImprintDB>>>(
  store: IDBPObjectStore<ImprintDB, Stores, "outbox", "readwrite">,
  userId: string,
): Promise<OutboxRecord[]> {
  if (await store.count() > MAX_PROGRESS_RECORDS + MAX_SESSION_RECORDS + MAX_MUTATION_RECORDS) {
    await store.clear();
    return [];
  }
  const [values, keys] = await Promise.all([store.getAll(), store.getAllKeys()]);
  const valid: OutboxRecord[] = [];
  for (let index = 0; index < values.length; index += 1) {
    const value: unknown = values[index];
    if (isValidOutboxRecord(value) && (value.userId === undefined || value.userId === userId)) valid.push(value);
    else await store.delete(keys[index]);
  }
  return valid;
}

function isValidOutboxRecord(value: unknown): value is OutboxRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Partial<OutboxRecord>;
  if (typeof record.key !== "string"
    || !record.key
    || (record.version !== undefined && (typeof record.version !== "string" || !record.version))
    || (record.userId !== undefined && (typeof record.userId !== "string" || !record.userId))
    || !validCanonicalItemId(record.itemId)
    || typeof record.createdAt !== "string"
    || !Number.isFinite(Date.parse(record.createdAt))) return false;
  if (record.kind === "progress") {
    const payload = record.payload as { reading_progress?: unknown } | undefined;
    return record.key === `progress:${record.itemId}`
      && Boolean(payload && typeof payload === "object" && !Array.isArray(payload))
      && typeof payload?.reading_progress === "number"
      && Number.isFinite(payload.reading_progress)
      && payload.reading_progress >= 0
      && payload.reading_progress <= 1;
  }
  if (record.kind === "session") {
    return record.key.startsWith("session:")
      && record.key.length > "session:".length
      && isValidReadingSession(record.payload)
      && record.payload.item_id === record.itemId;
  }
  if (record.kind === "state") {
    const payload = record.payload as { status?: unknown; is_favorite?: unknown } | undefined;
    return record.key === `state:${record.itemId}` && Boolean(payload && typeof payload === "object" && !Array.isArray(payload))
      && (payload?.status === undefined || payload.status === "inbox" || payload.status === "queue" || payload.status === "archive")
      && (payload?.is_favorite === undefined || typeof payload.is_favorite === "boolean")
      && (payload?.status !== undefined || payload?.is_favorite !== undefined);
  }
  if (record.kind === "annotation") {
    const payload = record.payload as { annotation_id?: unknown; note?: unknown } | undefined;
    return typeof payload?.annotation_id === "string" && /^ann_[a-zA-Z0-9_-]{1,128}$/.test(payload.annotation_id)
      && record.key === `annotation:${payload.annotation_id}` && typeof payload.note === "string" && payload.note.length <= 10_000;
  }
  return false;
}

function isValidReadingSession(value: unknown): value is ReadingSessionInput {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const session = value as Partial<ReadingSessionInput>;
  const startedAt = typeof session.started_at === "string" ? Date.parse(session.started_at) : Number.NaN;
  const endedAt = typeof session.ended_at === "string" ? Date.parse(session.ended_at) : Number.NaN;
  return validCanonicalItemId(session.item_id)
    && typeof session.started_at === "string"
    && Number.isFinite(startedAt)
    && typeof session.ended_at === "string"
    && Number.isFinite(endedAt)
    && endedAt >= startedAt
    && typeof session.seconds_read === "number"
    && Number.isFinite(session.seconds_read)
    && session.seconds_read > 0
    && session.seconds_read <= 24 * 60 * 60
    && typeof session.progress_start === "number"
    && Number.isFinite(session.progress_start)
    && session.progress_start >= 0
    && session.progress_start <= 1
    && typeof session.progress_end === "number"
    && Number.isFinite(session.progress_end)
    && session.progress_end >= 0
    && session.progress_end <= 1
    && session.source === "web"
    && Boolean(session.metadata && typeof session.metadata === "object" && !Array.isArray(session.metadata))
    && typeof session.metadata?.reason === "string";
}

function compareOutboxRecords(left: OutboxRecord, right: OutboxRecord): number {
  return left.createdAt.localeCompare(right.createdAt) || left.key.localeCompare(right.key);
}

export function loadPreferences(): ReaderPreferences {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return { ...DEFAULT_PREFERENCES };
    const candidate = stored as Partial<Record<keyof ReaderPreferences, unknown>>;
    return {
      typeSize: candidate.typeSize === "s" || candidate.typeSize === "m" || candidate.typeSize === "l" ? candidate.typeSize : DEFAULT_PREFERENCES.typeSize,
      width: candidate.width === "narrow" || candidate.width === "wide" ? candidate.width : DEFAULT_PREFERENCES.width,
      theme: candidate.theme === "paper" || candidate.theme === "white" ? candidate.theme : DEFAULT_PREFERENCES.theme,
    };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function savePreferences(preferences: ReaderPreferences): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(preferences));
  } catch {
    // Reading preferences remain active in memory when browser storage is unavailable.
  }
}

export async function clearOfflineReadingData(userId: string): Promise<void> {
  const database = await dbPromise;
  const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
  const session: unknown = await transaction.objectStore("meta").get("session");
  if (!sessionBelongsTo(session, userId)) { await transaction.done; return; }
  await Promise.all(ALL_STORE_NAMES.map((name) => transaction.objectStore(name).clear()));
  // Keep authentication while invalidating outstanding artwork/cache contexts.
  await transaction.objectStore("meta").put({ ...(session as SessionRecord), generation: newSessionGeneration() });
  await transaction.done;
  localStorage.removeItem(PREFS_KEY);
}

export async function clearOfflineData(): Promise<void> {
  await purgeOfflineData(await dbPromise);
}

async function purgeOfflineData(database: Awaited<typeof dbPromise>): Promise<void> {
  const transaction = database.transaction(ALL_STORE_NAMES, "readwrite");
  await Promise.all(ALL_STORE_NAMES.map((name) => transaction.objectStore(name).clear()));
  await transaction.done;
  localStorage.removeItem(PREFS_KEY);
  // Workbox CacheStorage contains only the public app shell and navigation fallback.
  // Preserve it so clearing private reader data does not strand an installed PWA offline.
}
